import { Users } from 'lucide-react'
import { readSettingMode, isFixedGameSetting } from '@repo/sdk'

import { formatGameSettingDisplay, parseGameSettingToPlayerConfig } from '@/lib/play'
import { useI18n } from '@/lib/i18n'

export function RoomSetup({
  gameSetting,
  seatCount,
  compact = false,
}: {
  gameSetting: string | null | undefined
  seatCount: number | null | undefined
  compact?: boolean
}) {
  const { t } = useI18n()
  if (!gameSetting)
    return null

  const settingMode = readSettingMode(gameSetting)
  const isRandomMode = settingMode.terminalSuffix === 'R'
    || settingMode.symbolicPlayerCount !== null
  const parsed = isRandomMode ? null : parseGameSettingToPlayerConfig(gameSetting)

  const named = t(`gameSettings.${isRandomMode ? 'G10_ULTIMATE_TRIAL' : gameSetting}`)
  const roleLine = parsed
    ? isFixedGameSetting(gameSetting)
      ? `${named} · ${formatGameSettingDisplay(parsed, t, { includeEmoji: compact })}`
      : formatGameSettingDisplay(parsed, t, { includeEmoji: compact })
    : (named === `gameSettings.${gameSetting}` ? null : named)

  const players = seatCount ?? parsed?.playerCount ?? null

  if (compact) {
    const detail = roleLine ?? gameSetting

    return (
      <p className="text-xs text-muted-foreground" data-testid="room-setup-compact">
        {players === null
          ? detail
          : `${t('play.players', { count: players })} · ${detail}`}
      </p>
    )
  }

  return (
    <div className="space-y-1 rounded-lg border bg-muted/30 p-3" data-testid="room-setup">
      <div className="flex items-center gap-2 text-sm font-medium">
        <Users className="size-4 shrink-0" aria-hidden="true" />
        <span>
          {players === null ? t('room.setup.title') : t('play.players', { count: players })}
        </span>
      </div>

      {isRandomMode
        ? (
            <div className="space-y-1 text-xs text-muted-foreground" data-testid="room-setup-random">
              <p>{t('game.g10RandomInfoEvil')}</p>
              <p>{t('game.g10RandomInfoGood')}</p>
            </div>
          )
        : roleLine
          ? (
              <p className="text-xs text-muted-foreground" data-testid="room-setup-roles">
                {roleLine}
              </p>
            )
          : null}
    </div>
  )
}
